import clsx from 'clsx';
import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { useMeasure } from '../../hooks/useMeasure';
import Icon from '../Icon/Icon';
import styles from './HorizontalOverflow.module.css';

type Props = {
  scrollWidth?: number;
  children: ReactNode;
  roundedCorner?: boolean;
  showArrows?: boolean;
};

export default function HorizontalOverflow({
  scrollWidth = 120,
  children,
  roundedCorner = false,
  showArrows = false,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const dimensions = useMeasure(scrollContainerRef);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(false);

  useEffect(() => {
    setShowLeft(Math.round(scrollContainerRef.current?.scrollLeft || 0) > 0);
    setShowRight(
      Math.round(scrollContainerRef.current?.clientWidth || 0) <
        (scrollContainerRef.current?.scrollWidth || 0)
    );
  }, [dimensions]);

  function handleScroll(event: React.UIEvent<HTMLDivElement>) {
    setShowLeft(event.currentTarget.scrollLeft > 0);
    setShowRight(
      event.currentTarget.scrollLeft <
        event.currentTarget.scrollWidth - event.currentTarget.clientWidth
    );
  }

  return (
    <div className={styles.container} ref={containerRef}>
      <button
        type="button"
        className={clsx(styles.arrow, styles.left, {
          [styles.visible]: showArrows && showLeft,
        })}
        onClick={() => {
          if (scrollContainerRef.current) {
            scrollContainerRef.current?.scrollTo({
              left: scrollContainerRef.current.scrollLeft - scrollWidth,
              behavior: 'smooth',
            });
          }
        }}
      >
        <Icon name="caretLeft" size="small" />
      </button>

      <div
        className={clsx(styles.scrollContainer, {
          [styles.roundedCorners]: roundedCorner,
        })}
        ref={scrollContainerRef}
        onScroll={handleScroll}
      >
        {children}
      </div>

      <button
        type="button"
        className={clsx(styles.arrow, styles.right, {
          [styles.visible]: showArrows && showRight,
        })}
        onClick={() => {
          if (scrollContainerRef.current) {
            scrollContainerRef.current?.scrollTo({
              left: scrollContainerRef.current.scrollLeft + scrollWidth,
              behavior: 'smooth',
            });
          }
        }}
      >
        <Icon name="caretRight" size="small" />
      </button>
    </div>
  );
}
